// useGtm.ts
// Minimal, robust GTM helper + React hook

type DL = Record<string, any>;

// ——— dataLayer buffer (helps with SSR/Next.js hydration) ———
const dlQueue: DL[] = [];

// Low-level push (safe with/without window)
function _pushToDataLayer(obj: DL) {
  if (typeof window === "undefined") {
    dlQueue.push(obj);
    return;
  }
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push(obj);
}

// Flush any buffered pushes once we're on the client
export function flushDataLayerQueue() {
  if (typeof window === "undefined") return;
  if (!dlQueue.length) return;
  (window as any).dataLayer = (window as any).dataLayer || [];
  for (const item of dlQueue) (window as any).dataLayer.push(item);
  dlQueue.length = 0;
}

// ——— Public: plain function (use anywhere, even outside React) ———
/**
 * Fire a GTM/GA4 event.
 * @param event  Event name (e.g., 'bot_launch_click')
 * @param params Arbitrary event parameters
 * @param value  Optional numeric value (GA4-friendly)
 * @param defaults Optional defaults merged into every payload
 */
export function trackEvent(
  event: string,
  params: DL = {},
  value?: number,
  defaults?: DL
) {
  // Build payload. Put `event` LAST so it cannot be overwritten.
  const payload: DL = { ...(defaults || {}), ...params };
  if (typeof value === "number" && !Number.isNaN(value)) payload.value = value;
  payload.event = event;
  
  _pushToDataLayer(payload);
}

// ——— Debug helpers ———
/**
 * Debug helper to check GTM events in console
 * Usage: window.checkGTMEvents() or window.checkGTMEvents('bot_launch_click')
 */
if (typeof window !== "undefined") {
  (window as any).checkGTMEvents = (eventName?: string) => {
    const dataLayer = (window as any).dataLayer || [];
    if (eventName) {
      const filteredEvents = dataLayer.filter((item: any) => item.event === eventName);
      return filteredEvents;
    } else {
      return dataLayer;
    }
  };
  
  (window as any).getLastGTMEvent = () => {
    const dataLayer = (window as any).dataLayer || [];
    const lastEvent = dataLayer[dataLayer.length - 1];
    return lastEvent;
  };
}

// ——— React Hook ———
import { useCallback, useEffect, useRef } from "react";

/**
 * React hook returning a memoized tracker with optional defaults.
 * Usage: const track = useGtmEvent({ app: 'autobot' });
 *        track('bot_launch_click', { bot_name: 'V8' }, 1000)
 */
export function useGtmEvent(defaults?: DL) {
  // ensure one-time client flush after mount
  const flushed = useRef(false);
  useEffect(() => {
    if (!flushed.current) {
      flushDataLayerQueue();
      flushed.current = true;
    }
  }, []);

  return useCallback(
    (event: string, params: DL = {}, value?: number) => {
      trackEvent(event, params, value, defaults);
    },
    [defaults]
  );
}
